import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Mail, Lock, User, Phone, UserCog, Users, Handshake } from 'lucide-react';
import { autoFormatPhone, formatCanadianPhone } from '@/utils/phone';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const urlRole = searchParams.get('role');
  const isFacilityInvite = urlRole === 'facility_manager';
  const [isRegister, setIsRegister] = useState(!!inviteToken);
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: isFacilityInvite ? 'facility_manager' : 'parent', phone: '' });

  useEffect(() => {
    if (inviteToken && isFacilityInvite) {
      // Facility manager invite
      api.get(`/invites/facility/${inviteToken}`)
        .then(r => {
          setInviteInfo({ type: 'facility', facility_name: r.data.facility_name });
          setForm(prev => ({ ...prev, email: r.data.email || '', role: 'facility_manager' }));
        })
        .catch(() => toast.error('Invalid or expired facility invite link'));
    } else if (inviteToken) {
      // Parent invite
      api.get(`/invites/token/${inviteToken}`)
        .then(r => {
          setInviteInfo({ type: 'parent', ...r.data });
          setForm(prev => ({ ...prev, email: r.data.parent_email || '', role: 'parent' }));
        })
        .catch(() => toast.error('Invalid or expired invite link'));
    }
  }, [inviteToken, isFacilityInvite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        const regData = { ...form };
        if (inviteToken) regData.invite_token = inviteToken;
        // Normalize phone to E.164
        if (regData.phone) {
          const normalized = formatCanadianPhone(regData.phone);
          if (!normalized) {
            toast.error('Please enter a valid Canadian phone number');
            setLoading(false);
            return;
          }
          regData.phone = normalized;
        }
        await register(regData);
        toast.success('Account created successfully!');
      } else {
        await login(form.email, form.password);
        toast.success('Welcome back!');
      }
      setTimeout(() => navigate('/dashboard', { replace: true }), 100);
    } catch (err) {
      let msg;
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err.code === 'ERR_NETWORK') {
        msg = 'Cannot connect to server. Please check your connection and try again.';
      } else {
        msg = err.message || 'Authentication failed. Please try again.';
      }
      toast.error(msg);
      // If no account found, suggest switching to Sign Up
      if (msg.includes('No account found')) {
        setIsRegister(true);
        toast.info('Switched to Sign Up — create your account below', { duration: 5000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - branding with dark navy theme */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1E1B4B] items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#22D3EE]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4F017]/5 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-lg text-center relative z-10">
          <div className="flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Big Effort Swim" className="w-40 h-40 object-contain drop-shadow-2xl" />
          </div>
          <h1 className="text-4xl font-extrabold text-white tracking-tight mb-2" style={{ fontFamily: 'Nunito' }}>
            <span className="text-[#C4F017]">SplashAble</span>
          </h1>
          <p className="text-sm text-[#22D3EE] font-semibold tracking-widest uppercase mb-6">by Big Effort Swim</p>
          <p className="text-lg text-slate-300 leading-relaxed mb-8" style={{ fontFamily: 'DM Sans' }}>
            Empowering neurodivergent children to master swimming through structured, visual-first instruction.
          </p>
          <div className="grid grid-cols-2 gap-4 text-left">
            {[
              { icon: '🏊', label: 'Visual Schedules' },
              { icon: '🎬', label: 'Video Storyboards' },
              { icon: '📊', label: 'Progress Tracking' },
              { icon: '🌟', label: 'Reward Systems' },
            ].map((f, i) => (
              <div key={`feat-${i}`} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                <span className="text-2xl">{f.icon}</span>
                <span className="font-semibold text-white text-sm">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - auth form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F0FDFA]">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border-0" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex flex-col items-center gap-2 mb-4">
              <img src="/logo.png" alt="Big Effort Swim" className="w-20 h-20 object-contain" />
              <div>
                <span className="text-xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>SplashAble </span>
                <span className="text-xs font-bold text-[#22D3EE]">by Big Effort Swim</span>
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {isRegister ? 'Join the SplashAble community' : 'Sign in to continue'}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Invite Banner */}
            {inviteInfo && inviteInfo.type === 'parent' && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4" data-testid="invite-banner">
                <div className="flex items-center gap-3">
                  <Handshake className="w-5 h-5 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-green-800">You're invited!</p>
                    <p className="text-xs text-green-600">
                      {inviteInfo.instructor_name} invited you to join as <strong>{inviteInfo.child_name}</strong>'s parent.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {inviteInfo && inviteInfo.type === 'facility' && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4 mb-4" data-testid="facility-invite-banner">
                <div className="flex items-center gap-3">
                  <Handshake className="w-5 h-5 text-cyan-600 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-cyan-800">Facility Manager Invite</p>
                    <p className="text-xs text-cyan-600">
                      Create your account for <strong>{inviteInfo.facility_name}</strong>. This is a shared facility account.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label className="text-slate-600 font-semibold mb-2 block">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <Input
                        data-testid="register-name-input"
                        className="pl-10 h-12 rounded-xl border-slate-200"
                        placeholder="Your name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                  </div>
                  {!inviteToken && (
                  <div>
                    <Label className="text-slate-600 font-semibold mb-2 block">I am a...</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        data-testid="role-select-instructor"
                        onClick={() => setForm({ ...form, role: 'instructor' })}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold ${
                          form.role === 'instructor'
                            ? 'border-[#22D3EE] bg-[#ECFEFF] text-[#0E7490]'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        <UserCog className="w-5 h-5" /> Instructor
                      </button>
                      <button
                        type="button"
                        data-testid="role-select-parent"
                        onClick={() => setForm({ ...form, role: 'parent' })}
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-semibold ${
                          form.role === 'parent'
                            ? 'border-[#22D3EE] bg-[#ECFEFF] text-[#0E7490]'
                            : 'border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        <Users className="w-5 h-5" /> Parent
                      </button>
                    </div>
                  </div>
                  )}
                  <div>
                    <Label className="text-slate-600 font-semibold mb-2 block">Phone (Canadian format)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <Input
                        data-testid="register-phone-input"
                        className="pl-10 h-12 rounded-xl border-slate-200"
                        placeholder="(250) 555-1234"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: autoFormatPhone(e.target.value) })}
                        maxLength={14}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Canadian number for SMS notifications</p>
                  </div>
                </>
              )}

              <div>
                <Label className="text-slate-600 font-semibold mb-2 block">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    data-testid="email-input"
                    type="email"
                    className="pl-10 h-12 rounded-xl border-slate-200"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-600 font-semibold mb-2 block">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    data-testid="password-input"
                    type="password"
                    className="pl-10 h-12 rounded-xl border-slate-200"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </div>
              </div>

              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95"
              >
                {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-400">or continue with</span></div>
            </div>

            <Button
              data-testid="google-auth-btn"
              variant="outline"
              onClick={handleGoogleAuth}
              className="w-full h-12 rounded-full border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-semibold gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </Button>

            <p className="text-center mt-6 text-slate-500">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                data-testid="toggle-auth-mode"
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-[#22D3EE] font-semibold hover:underline"
              >
                {isRegister ? 'Sign In' : 'Sign Up'}
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
